import { LoginEventDetail } from "../types/CommonSsoLogin/CommonSsoLoginTypes";
import { getUserDetailsWithoutWrapper } from "../services/demogApi";
import { logout, setPerpetualToken } from "../services/loginApi";
import {
  CPR_ENTRY_POINT,
  CPR_FORM_DATA_KEY,
  CP_COOKIE_CONSTANTS,
  FPR_DATA_STORAGE_KEY,
} from "../constants/fpr.constants";
import {
  AUTH_TYPE,
  CHECK_COOKIE,
  EXPIRED_TIME,
  LSFLAG,
  NOTIFICATION_ENABLED,
  PERPETUAL_TOKEN,
  TOKEN_EXPIRY,
  USER_DETAILS,
} from "../constants/sso.constants";
import { logErrorToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { getCookie, deleteCookie } from "@repo/shared-utils/common-utils";
import { initLoginSession } from "./session";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import {
  getCustomerDemogFromApp,
  getDemogPromptData,
  setDemogStorage,
  setSessionData,
} from "./utils";
import { setLoginStatus, store } from "@repo/global-store";
import { COMMON_SSO_ACCESS_TOKEN } from "@repo/shared-utils/sso.constants";

export type LoginStatus = "success" | "failure" | "pending" | "perpetual";

/**
 * Logs in a user by managing the SSO modal state and authentication flow.
 *
 * @param {string} [mobileNo] - Optional mobile number. If provided, initializes modal with the number
 * @param {boolean} [openOtpModal=false] - Optional flag to directly open OTP modal. Defaults to false
 * @returns {Promise<void>} Resolves when user is successfully authenticated
 * @throws Will throw an error if authentication fails
 */
export async function loginUser(_mobileNo?: string, _openOtpModal = false): Promise<void> {
  await createCommonSsoLoginPromise();
}

/**
 * Creates a promise that resolves when the "bflLoginSuccess" event is fired
 * and rejects if the "bflLoginFailure" event is fired.
 *
 * The promise resolves after setting the authentication token, closing the modal,
 * and initializing the user session.
 *
 * @returns {Promise<void>} A promise that resolves when the login process is successful.
 */
async function createCommonSsoLoginPromise(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    // Success handler: process login details, close modal, initialize session.
    const handleSuccess = async (event: CustomEvent<LoginEventDetail>) => {
      const { data } = event.detail;
      const { token, mobile } = data;
      if (token && mobile) {
        await initLoginSession(mobile);
        await setPerpetualToken(mobile);
        cleanupListeners();
        resolve();
      }
    };

    // Failure handler: update login status and reject the promise.
    const handleFailure = (event: CustomEvent<LoginEventDetail>) => {
      const { statusCode, message } = event.detail;
      store.dispatch(setLoginStatus({ loginStatus: "failure" }));
      cleanupListeners();
      logErrorToNewRelic("login failed", { statusCode, message });
      reject(event.detail);
    };

    // Helper function to remove both event listeners.
    const cleanupListeners = () => {
      window.removeEventListener("bflLoginSuccess", handleSuccess as unknown as EventListener);
      window.removeEventListener("bflLoginFailure", handleFailure as unknown as EventListener);
    };

    window.addEventListener("bflLoginSuccess", handleSuccess as unknown as EventListener);
    window.addEventListener("bflLoginFailure", handleFailure as unknown as EventListener);
  });
}

// WARNING: DO NOT USE THIS FUNCTION DIRECTLY
export async function setUserDetails(mobileNo: string): Promise<void> {
  try {
    if (isOpenInApp()) {
      const userDetails = getDemogPromptData(getCustomerDemogFromApp());
      await setDemogStorage(userDetails, mobileNo);
      setDemogApiData(mobileNo);
    } else {
      await setDemogApiData(mobileNo);
    }
  } catch (error) {
    console.error(error, { reason: "Failed setting user details" });
    logErrorToNewRelic(error as Error, {
      message: `Failed setting user details: ${(error as Error).message}`,
    });
  }
}

const setDemogApiData = async (mobileNo: string) => {
  const response = await getUserDetailsWithoutWrapper(mobileNo);
  await setDemogStorage(response, mobileNo);
};

export function clearSession() {
  // Reset all the keys that are not in PerpetualSessionKeys
  Promise.all([
    setSessionData("city", ""),
    setSessionData("email", ""),
    setSessionData("requestId", ""),
    setSessionData("dob", ""),
    setSessionData("lastName", ""),
    setSessionData("isOffers", ""),
    setSessionData("city", ""),
    setSessionData("dealerId", ""),
    setSessionData("cndFlag", ""),
    setSessionData("dealerName", ""),
    setSessionData("reKycFlag", ""),
    setSessionData("kycRenewalDate", ""),
    setSessionData("pinCode", ""),
    setSessionData("indivCorpFlag", ""),
  ]);
  localStorage.removeItem(COMMON_SSO_ACCESS_TOKEN);
  store.dispatch(setLoginStatus({ loginStatus: "failure" }));
  deleteCookie(CHECK_COOKIE);
  deleteCookie(AUTH_TYPE);
  deleteCookie(CP_COOKIE_CONSTANTS.cScore);
  deleteCookie(CP_COOKIE_CONSTANTS.cpss);
  deleteCookie(CP_COOKIE_CONSTANTS.fsup);
  deleteCookie(CP_COOKIE_CONSTANTS.redirectionUrl);
  deleteCookie(CP_COOKIE_CONSTANTS.planCode);
  localStorage.removeItem(CP_COOKIE_CONSTANTS.cpsd);
  localStorage.removeItem(TOKEN_EXPIRY);
  localStorage.removeItem(EXPIRED_TIME);
  localStorage.removeItem(FPR_DATA_STORAGE_KEY);
  localStorage.removeItem(CPR_ENTRY_POINT);
  localStorage.removeItem(CPR_FORM_DATA_KEY);
  localStorage.removeItem(NOTIFICATION_ENABLED);
  // this can be removed in the future, when react project is removed
  deleteCookie(LSFLAG);
}

export function clearPerpetualSession() {
  localStorage.removeItem(COMMON_SSO_ACCESS_TOKEN);
  store.dispatch(setLoginStatus({ loginStatus: "failure" }));
  deleteCookie(USER_DETAILS);
  deleteCookie(CHECK_COOKIE);
  deleteCookie(PERPETUAL_TOKEN);
  deleteCookie(AUTH_TYPE);
  deleteCookie(CP_COOKIE_CONSTANTS.cScore);
  deleteCookie(CP_COOKIE_CONSTANTS.cpss);
  deleteCookie(CP_COOKIE_CONSTANTS.fsup);
  deleteCookie(CP_COOKIE_CONSTANTS.redirectionUrl);
  deleteCookie(CP_COOKIE_CONSTANTS.planCode);
  localStorage.removeItem(CP_COOKIE_CONSTANTS.cpsd);
  localStorage.removeItem(TOKEN_EXPIRY);
  localStorage.removeItem(EXPIRED_TIME);
  localStorage.removeItem(FPR_DATA_STORAGE_KEY);
  localStorage.removeItem(NOTIFICATION_ENABLED);
  // this can be removed in the future, when react project is removed
  deleteCookie(LSFLAG);
}

/**
 * This function logs out a user by clearing their session and optionally reloading the page.
 * If the logout API fails, it will clear the session.
 *
 * @param {string} mobileNo - The mobile number of the user.
 * @param {Object} options - An object containing options for the logout process.
 * @param {boolean} [options.withPerpetual=true] - Whether to use a perpetual session. If true, the function will logout user and delete perpetual session. If false, the function will clear the regular session but keep the perpetual sesion.
 * @param {boolean} [options.withReload=true] - Whether to reload the page after logging out. If true or undefined, the function will reload the page.
 *
 * @returns {Promise<void>} A Promise that resolves when the logout process is complete.
 *
 * @throws If any error occurs during the process.
 */
export async function logoutUser(
  mobileNo: string,
  options: {
    withPerpetual?: boolean;
    withReload?: boolean;
  } = { withPerpetual: true, withReload: true }
): Promise<void> {
  try {
    if (options.withPerpetual) {
      const perpetualToken = getCookie(PERPETUAL_TOKEN) || "";
      await logout(mobileNo, perpetualToken);
      clearPerpetualSession();
    } else {
      await logout(mobileNo);
      clearSession();
    }
  } catch (error) {
    if (options.withPerpetual) {
      clearPerpetualSession();
    } else {
      clearSession();
    }
    console.error(error);
  }

  if (options.withReload || options.withReload === undefined) {
    window.location.reload();
  }
  store.dispatch(setLoginStatus({ loginStatus: "failure" }));
}
